import { useMemo, useState } from "react";
import { Link } from "react-router";
import { useTranslation } from "@repo/i18n";
import { Button } from "@repo/components";
import { css } from "@repo/styled-system/css";
import { useVenue } from "../shell/VenueContext";
import { useMinuteClock } from "../shell/useMinuteClock";
import { useServices } from "../bookings/queries";
import { todayServiceDay, useArrive, useSeat, useTimeline } from "./queries";
import { liveStyles as s } from "./styles";
import { WalkInForm } from "./WalkInForm";
import type { LiveRow } from "./types";

/**
 * The phone-first DOOR view (`/v/:venueId/door`, tasks/live-service-and-floor.md): thumb-reach next
 * arrivals with arrive/seat one-taps and the prominent shared walk-in control (party size + optional
 * name/phone). >= 44px targets (RGAA baseline).
 */
export function DoorView() {
  const { t } = useTranslation();
  const { venueId, venue } = useVenue();
  const serviceDay = useMemo(() => todayServiceDay(venue.timeZone), [venue.timeZone]);

  const timeline = useTimeline(venueId, serviceDay);
  const services = useServices(venueId);
  const arrive = useArrive(venueId);
  const seat = useSeat(venueId);
  const [actionError, setActionError] = useState<string | null>(null);

  // The walk-in service: the current booking's service if the floor has one, else fall back to the
  // venue's first configured service — so walk-ins work early in service when the timeline is empty.
  const walkInServiceId = firstServiceId(timeline.data) ?? services.data?.[0]?.id;

  // Not `Date.now()` inline — react-compiler forbids impure calls during render (react(purity)).
  const nowSec = useMinuteClock();
  const upcoming = (timeline.data ?? [])
    .filter((r) => r.status === "CONFIRMED")
    .filter((r) => r.startSec >= nowSec - 3600)
    .toSorted((a, b) => a.startSec - b.startSec)
    .slice(0, 12);

  return (
    <div className={s.doorPage}>
      <header className={s.header}>
        <h1 className={s.title}>{t("door.title")}</h1>
        <Link to={`/v/${venueId}/live`} className={css({ textDecoration: "underline" })}>
          {t("door.openLive")}
        </Link>
      </header>

      <WalkInForm venueId={venueId} serviceId={walkInServiceId} prominent />

      <section aria-labelledby="arrivals-heading" className={s.section}>
        <h2 id="arrivals-heading" className={s.sectionTitle}>
          {t("door.nextArrivals")}
        </h2>
        {timeline.isLoading && <p className={s.empty}>{t("live.loading")}</p>}
        {timeline.isError && (
          <p role="alert" className={css({ color: "fg.error" })}>
            {t("live.loadError")}
          </p>
        )}
        {actionError && (
          <p role="alert" className={css({ color: "fg.error" })}>
            {actionError}
          </p>
        )}
        {timeline.data && upcoming.length === 0 && (
          <p className={s.empty}>{t("door.noArrivals")}</p>
        )}
        <ul className={s.rows}>
          {upcoming.map((row) => (
            <li key={row.bookingId} className={s.arrival}>
              <div className={s.rowMain}>
                <span className={s.rowTime}>{formatTime(row.startSec, venue.timeZone)}</span>
                <span className={s.rowGuest}>{guestName(row)}</span>
                <span className={s.rowMeta}>{t("live.party", { n: row.partySize })}</span>
                {row.arrivedAtSec !== null && (
                  <span className={s.arrived}>{t("door.arrived")}</span>
                )}
              </div>
              <div className={s.rowActions}>
                {row.arrivedAtSec === null && (
                  <Button
                    variant="outline"
                    disabled={arrive.isPending}
                    onClick={() => {
                      setActionError(null);
                      arrive.mutate(
                        { bookingId: row.bookingId, version: row.version },
                        { onError: () => setActionError(t("live.actionFailed")) },
                      );
                    }}
                  >
                    {t("live.action.arrive")}
                  </Button>
                )}
                <Button
                  variant="solid"
                  disabled={seat.isPending}
                  onClick={() => {
                    setActionError(null);
                    seat.mutate(
                      { bookingId: row.bookingId, body: { version: row.version, tableIds: [] } },
                      { onError: () => setActionError(t("live.actionFailed")) },
                    );
                  }}
                >
                  {t("live.action.seat")}
                </Button>
              </div>
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}

function guestName(row: LiveRow): string {
  return [row.guestGivenName, row.guestFamilyName].filter(Boolean).join(" ") || "—";
}

function firstServiceId(rows: LiveRow[] | undefined): string | undefined {
  return rows?.[0]?.serviceId;
}

function formatTime(startSec: number, timeZone: string): string {
  try {
    return new Intl.DateTimeFormat("fr-FR", {
      timeZone,
      hour: "2-digit",
      minute: "2-digit",
    }).format(startSec * 1000);
  } catch {
    return new Date(startSec * 1000).toISOString().slice(11, 16);
  }
}
